import { RoomType } from "building-types/rooms/roomTypesEnum";
import { SpaceType } from "building-types/rooms/spaceTypesEnum";

// lower is generally more public
export const SpaceAccessRankingTable: { [key in SpaceType]: number } = {
  [SpaceType.EXTERIOR]: 10,
  [SpaceType.EXTERIOR_COVERED]: 10,

  [SpaceType.EXTERIOR_BACK]: 20, //todo: this should likely take street orientation into consideration
  [SpaceType.EXTERIOR_COVERED_BACK]: 20, //todo: this should likely take street orientation into consideration

  [SpaceType.TRANSITION]: 30,
  [SpaceType.CIRCULATION]: 40,

  [SpaceType.GATHERING]: 50, // synthesized type of dining, kitchen, living
  [SpaceType.DINING]: 60,
  [SpaceType.KITCHEN]: 70,
  [SpaceType.LIVING]: 80,
  [SpaceType.GENERAL]: 85,

  [SpaceType.CONTEMPLATIVE]: 90,
  [SpaceType.BEDROOM]: 100,
  [SpaceType.BATHROOM]: 110,

  [SpaceType.UTILITY]: 120,
  [SpaceType.STORAGE]: 130,

  [SpaceType.UNCLASSIFIED]: 140,
};

export type RoomSetSpaceTypesTable = { [roomId: string]: SpaceType };

export interface RoomSetSpaceTypes {
  majoritySpaceType: SpaceType;
  roomSpaceTypes: RoomSetSpaceTypesTable;
}

export interface ElectricalSpaceClassifier {
  is: SpaceType;
  isInGroupOf?: SpaceType[];
}

export interface SpaceClassifierTables {
  primaryClassifier: { [key in RoomType]?: ElectricalSpaceClassifier };
  secondaryClassifier: { [key in RoomType]?: ElectricalSpaceClassifier };
}

export const electricalSpaceClassifiers: { [key in RoomType]: ElectricalSpaceClassifier } = {
  // exterior
  [RoomType.WALK]: { is: SpaceType.EXTERIOR },
  [RoomType.PATIO]: { is: SpaceType.EXTERIOR },
  [RoomType.LANDSCAPE]: { is: SpaceType.EXTERIOR },

  // exterior covered
  [RoomType.BREEZEWAY]: { is: SpaceType.EXTERIOR_COVERED },
  [RoomType.FRONT_PORCH]: { is: SpaceType.EXTERIOR_COVERED },
  [RoomType.PORTICO]: { is: SpaceType.EXTERIOR_COVERED },
  [RoomType.REAR_PORCH]: { is: SpaceType.EXTERIOR_COVERED_BACK },
  [RoomType.SOLARIUM]: { is: SpaceType.EXTERIOR_COVERED_BACK },
  [RoomType.SCREEN]: { is: SpaceType.EXTERIOR_COVERED_BACK },

  // utility
  [RoomType.MECH]: { is: SpaceType.STORAGE, isInGroupOf: [SpaceType.UTILITY] },
  [RoomType.GARAGE_DETACH]: { is: SpaceType.UTILITY },
  [RoomType.GARAGE]: { is: SpaceType.UTILITY },
  [RoomType.WORKSHOP]: { is: SpaceType.UTILITY },

  // // circulation-transition
  [RoomType.FOYER]: { is: SpaceType.TRANSITION },
  [RoomType.ENTRY]: { is: SpaceType.TRANSITION },
  [RoomType.ENTRY_BUFFER]: { is: SpaceType.TRANSITION },
  [RoomType.MUDROOM]: { is: SpaceType.TRANSITION },
  [RoomType.MUD_LAUNDRY]: { is: SpaceType.TRANSITION },

  [RoomType.DOG]: { is: SpaceType.TRANSITION },

  // // circulation
  [RoomType.STAIRS]: { is: SpaceType.CIRCULATION },
  [RoomType.STAIRS_EDITOR]: { is: SpaceType.CIRCULATION },
  [RoomType.HALL]: { is: SpaceType.CIRCULATION },
  [RoomType.HALL_NO_WINDOW]: { is: SpaceType.CIRCULATION },
  [RoomType.HALL_NO_ENTRY]: { is: SpaceType.CIRCULATION },
  [RoomType.CORNER_HALL]: { is: SpaceType.CIRCULATION },
  [RoomType.UTIL_HALL]: { is: SpaceType.CIRCULATION },
  [RoomType.JUMP]: { is: SpaceType.CIRCULATION },
  [RoomType.HALL_CASED_OPENING]: { is: SpaceType.CIRCULATION },

  // utility or circulation
  [RoomType.LAUNDRY]: { is: SpaceType.UTILITY, isInGroupOf: [SpaceType.CIRCULATION] },

  // circulation to gathering
  [RoomType.LIVING_HALL]: { is: SpaceType.CIRCULATION },
  [RoomType.KITCHEN_HALL]: { is: SpaceType.CIRCULATION },

  // gathering
  [RoomType.LIVING]: { is: SpaceType.LIVING },
  [RoomType.FORMAL_LIVING]: { is: SpaceType.LIVING },
  [RoomType.DINING]: { is: SpaceType.DINING },
  [RoomType.FORMAL_DINING]: { is: SpaceType.DINING },
  [RoomType.KITCHEN]: { is: SpaceType.KITCHEN },
  [RoomType.FLEX]: { is: SpaceType.CONTEMPLATIVE },
  [RoomType.DINING_NOOK]: { is: SpaceType.DINING, isInGroupOf: [SpaceType.LIVING, SpaceType.CIRCULATION] },
  [RoomType.PANTRY]: { is: SpaceType.STORAGE, isInGroupOf: [SpaceType.KITCHEN] },
  [RoomType.REFRIGERATOR]: { is: SpaceType.STORAGE, isInGroupOf: [SpaceType.KITCHEN] },
  [RoomType.KITCHEN_CABINETS]: { is: SpaceType.KITCHEN, isInGroupOf: [SpaceType.KITCHEN] },

  // general
  [RoomType.GENERAL_OPEN]: { is: SpaceType.GENERAL },
  [RoomType.GENERAL]: { is: SpaceType.GENERAL },

  // gathering / contemplative
  [RoomType.GALLERY]: {
    is: SpaceType.CONTEMPLATIVE,
    isInGroupOf: [SpaceType.LIVING, SpaceType.CIRCULATION],
  },
  [RoomType.LIBRARY]: { is: SpaceType.CONTEMPLATIVE, isInGroupOf: [SpaceType.LIVING] },
  [RoomType.OFFICE]: { is: SpaceType.CONTEMPLATIVE, isInGroupOf: [SpaceType.LIVING] },
  [RoomType.READING_NOOK]: { is: SpaceType.CONTEMPLATIVE, isInGroupOf: [SpaceType.LIVING] },
  [RoomType.BAY_WINDOW]: {
    is: SpaceType.CONTEMPLATIVE,
    isInGroupOf: [SpaceType.LIVING, SpaceType.DINING, SpaceType.CIRCULATION],
  },
  [RoomType.FREESTANDING_FIREPLACE]: {
    is: SpaceType.LIVING,
    isInGroupOf: [SpaceType.CIRCULATION, SpaceType.DINING],
  },
  [RoomType.FIREPLACE]: { is: SpaceType.LIVING, isInGroupOf: [SpaceType.CIRCULATION, SpaceType.DINING] },

  // bedroom
  [RoomType.MASTER_SUITE]: { is: SpaceType.BEDROOM },
  [RoomType.MASTER_BED]: { is: SpaceType.BEDROOM },
  [RoomType.GUEST_SUITE]: { is: SpaceType.BEDROOM },
  [RoomType.BEDROOM]: { is: SpaceType.BEDROOM },

  // bedroom or circulation components
  [RoomType.MASTER_HALL]: { is: SpaceType.CIRCULATION, isInGroupOf: [SpaceType.BEDROOM] },
  [RoomType.MASTER_SEAT]: {
    is: SpaceType.CONTEMPLATIVE,
    isInGroupOf: [SpaceType.BEDROOM, SpaceType.CIRCULATION],
  },
  [RoomType.MASTER_VESTIBULE]: { is: SpaceType.CIRCULATION, isInGroupOf: [SpaceType.BEDROOM] },
  [RoomType.BED_PASS]: { is: SpaceType.CIRCULATION, isInGroupOf: [SpaceType.BEDROOM] },

  // bathroom
  [RoomType.MASTER_BATH]: { is: SpaceType.BATHROOM },
  [RoomType.BATHFULL]: { is: SpaceType.BATHROOM },
  [RoomType.BATHFULL_1_DOOR]: { is: SpaceType.BATHROOM },
  [RoomType.JJ_BATH]: { is: SpaceType.BATHROOM },
  [RoomType.BED_BATH]: { is: SpaceType.BATHROOM },
  [RoomType.BATHHALF]: { is: SpaceType.BATHROOM },
  [RoomType.POWDER]: { is: SpaceType.BATHROOM },
  [RoomType.WATER_CLOSET]: { is: SpaceType.BATHROOM },

  // bathroom components
  [RoomType.BATH_HALL]: { is: SpaceType.BATHROOM },
  [RoomType.SHOWER]: { is: SpaceType.BATHROOM },
  [RoomType.MASTER_VANITY]: { is: SpaceType.BATHROOM },
  [RoomType.BATHTUB]: { is: SpaceType.BATHROOM },

  // bathroom or bedroom component
  [RoomType.MASTER_PASS]: {
    is: SpaceType.CIRCULATION,
    isInGroupOf: [SpaceType.BEDROOM, SpaceType.BATHROOM],
  },

  // storage
  [RoomType.MASTER_CLOSET]: { is: SpaceType.STORAGE },
  [RoomType.WALK_IN_CLOSET]: { is: SpaceType.STORAGE },
  [RoomType.REACH_CLOSET]: { is: SpaceType.STORAGE },
  [RoomType.BED_CLOSET]: { is: SpaceType.STORAGE },
  [RoomType.COAT_CLOSET]: { is: SpaceType.STORAGE },
  [RoomType.CLOSET]: { is: SpaceType.STORAGE },
  [RoomType.CABINETS]: { is: SpaceType.STORAGE, isInGroupOf: [SpaceType.KITCHEN, SpaceType.DINING] },

  // // unclassified
  [RoomType.NONE]: { is: SpaceType.UNCLASSIFIED },
  [RoomType.OPEN_BELOW]: { is: SpaceType.UNCLASSIFIED },
  [RoomType.CHASE]: { is: SpaceType.UNCLASSIFIED },
};
